/**
 * Sender-side memory of transfers created in this browser (localStorage). Lets the sender re-copy the
 * link, see download counts and destroy a transfer early. Contains the link (with key) — it is the
 * sender's own device; the server never receives it.
 */
export interface SentTransfer {
  transferId: string;
  token: string;
  link: string;
  createdAt: number;
  expiresAt: number | null;
  burn: boolean;
  protected: boolean;
  files: { name: string; size: number }[];
  destroyed?: boolean;
  receipt?: {
    v: number;
    transferId: string;
    deletedAt: number;
    fileCount: number;
    totalBytes: number;
    signature: string;
  };
}

const KEY = "limeshare.sent.v1";
const MAX = 50;

export function loadSent(): SentTransfer[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as SentTransfer[]) : [];
  } catch {
    return [];
  }
}

function persist(list: SentTransfer[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* storage full or disabled — history is best-effort */
  }
}

export function addSent(t: SentTransfer): void {
  persist([t, ...loadSent().filter((x) => x.transferId !== t.transferId)]);
}

export function removeSent(transferId: string): void {
  persist(loadSent().filter((x) => x.transferId !== transferId));
}

export function markDestroyed(transferId: string, receipt?: SentTransfer["receipt"]): void {
  const all = loadSent();
  const updated: SentTransfer[] = all.map((t) => (t.transferId === transferId ? { ...t, destroyed: true, ...(receipt ? { receipt } : {}) } : t));
  persist(updated);
}

/** Drop entries whose expiry passed more than a day ago. */
export function pruneSent(): SentTransfer[] {
  const cutoff = Date.now() - 86_400_000;
  const kept = loadSent().filter((x) => x.expiresAt === null || x.expiresAt > cutoff);
  persist(kept);
  return kept;
}
